import React, {Fragment, useContext, useEffect} from "react";
import {ApiUtility} from "../common/ApiUtility";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {useRenderableData} from "../common/hooks/useRenderableData";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {SubscriptionDetail} from "./SubscriptionDetail";
import {LeaveWithoutSavingWarningUtility} from "../common/LeaveWithoutSavingWarningUtility";
import {GlobalContext} from "../admin-frame/AdminFrame";
import {ConfirmCancelModal} from "../common/ConfirmCancelModal";
import {Subscription} from "./SubscriptionTypes";

interface ReadSubscriptionProps {
    cancel: Function;
    afterSuccessfulSave: Function;
    subscriptionId: number;
    onClickInvoice: Function;
}

interface ReadSubscriptionData {
    loading?: boolean;
    subscription?: Subscription,
    invoiceDefaultsUrl?: string,
    showConfirmCancelModal?: boolean
}

export let ReadSubscription = function (props: ReadSubscriptionProps) {
    const context = useContext(GlobalContext);
    const initialData: ReadSubscriptionData = {
        loading: true,
        subscription: undefined,
        invoiceDefaultsUrl: undefined,
        showConfirmCancelModal: false
    }
    const renderableData = useRenderableData(initialData);

    // on mount, fetch subscription from API and put it in state
    useEffect(function () {
        let dataUpdate: ReadSubscriptionData = {};
        renderableData.updateAndRender({loading: true})
        ApiUtility.getSubscription(props.subscriptionId)
            .then(function (subscription) {
                dataUpdate = {
                    subscription: subscription,
                    loading: false,
                }
                return ApiUtility.getDefaultInvoiceSettingsUrl()
            })
            .then(function (urlResponse) {
                dataUpdate.invoiceDefaultsUrl = urlResponse.url
                renderableData.updateAndRender(
                    dataUpdate
                );
            })
            .catch(function (error) {
                renderableData.updateAndRender({loading: false});
                console.log(`Error fetching subscription: `, error);
                MessageDisplayerUtility.error(`Error fetching subscription.`);
            });
    }, [props.subscriptionId]);

    function onClickSync() {
        renderableData.updateAndRender({loading: true})
        ApiUtility.syncSubscriptionInvoices(props.subscriptionId)
            .then((response) => {
                LeaveWithoutSavingWarningUtility.disableLeaveWithoutSavingWarnings(context);
                window.setTimeout(function () {
                    MessageDisplayerUtility.success(`Successfully synced subscription invoices.`);
                    props.afterSuccessfulSave(props.subscriptionId)
                }, 0)
            })
            .catch((err) => {
                renderableData.updateAndRender({loading: false});
                console && console.error(err);
                MessageDisplayerUtility.error(`An error occurred while syncing the subscription invoices.`);
            });
    }

    function onClickCancelSubscription() {
        renderableData.updateAndRender({showConfirmCancelModal: true});
    }

    function closeConfirmCancelModal() {
        renderableData.updateAndRender({showConfirmCancelModal: false});
    }

    function performCancelSubscription() {
        renderableData.updateAndRender({loading: true})
        ApiUtility.cancelSubscription(props.subscriptionId)
            .then((response) => {
                LeaveWithoutSavingWarningUtility.disableLeaveWithoutSavingWarnings(context);
                window.setTimeout(function () {
                    MessageDisplayerUtility.success(`Successfully canceled subscription.`);
                    props.afterSuccessfulSave(props.subscriptionId)
                }, 0)
            })
            .catch((err) => {
                renderableData.updateAndRender({loading: false});
                console && console.error(err);
                MessageDisplayerUtility.error(`An error occurred while canceling the subscription.`);
            });
    }

    return (
        <Fragment>
            <ConditionalRenderer if={renderableData.loading} renderer={() => (
                <OverlayLoadingIndicator/>
            )}/>
            <ConditionalRenderer if={renderableData.subscription} renderer={() => (
                <SubscriptionDetail
                    cancel={props.cancel}
                    subscription={renderableData.subscription}
                    invoiceDefaultsUrl={renderableData.invoiceDefaultsUrl}
                    onClickSync={onClickSync}
                    onClickInvoice={props.onClickInvoice}
                    onClickCancelSubscription={onClickCancelSubscription}
                />
            )}/>
            <ConditionalRenderer if={renderableData.showConfirmCancelModal} renderer={() => (
                <ConfirmCancelModal
                    cancel={closeConfirmCancelModal}
                    confirm={performCancelSubscription}
                    headerText="Confirm Canceling Subscription"
                    cancelButtonText="NO - ABORT"
                    confirmButtonText="YES - CANCEL SUBSCRIPTION"
                    warningMessage={[
                        "Are you sure you want to cancel this subscription?",
                        "This will cancel the subscription, and the subscription will stop generating invoices. This operation will not affect existing invoices for the subscription."
                    ]}
                />
            )}/>
        </Fragment>
    );
};
